import React, { useEffect, useState } from 'react';
import cn from 'classnames';

import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import SM from 'components/sidebar/appointment/wizard/Wizard.module.scss';
import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';

import CN from './EstimateModal.module.scss';

const UPLOAD_STATUS_KEYS = [
  'clientsEmailPage.topBar.estimateModal.uploadStatus1',
  'clientsEmailPage.topBar.estimateModal.uploadStatus2',
  'clientsEmailPage.topBar.estimateModal.uploadStatus3',
  'clientsEmailPage.topBar.estimateModal.uploadStatus4',
  'clientsEmailPage.topBar.estimateModal.uploadStatus5',
  'clientsEmailPage.topBar.estimateModal.uploadStatus6',
  'clientsEmailPage.topBar.estimateModal.uploadStatus7',
];

const MESSAGE_CYCLE_INTERVAL_MS = 2800;
const FADE_MS = 350;

const EstimateModal = ({
  isOpen,
  tag,
  onClose,
  estimate,
  onDesign,
  disabled,
  isDesignCreating,
  hasNoClients,
  uploadState,
  uploadProgress = 0,
  onTimeoutClose,
}) => {
  const { t } = useTranslation();

  const { expectedCount, cosPerEmail, expectedCost } = estimate ?? {};

  const isFine = uploadState === 'fine';
  const isLoading = uploadState === 'loading';
  const isComplete = uploadState === 'complete';

  const [statusIndex, setStatusIndex] = useState(0);
  const [statusVisible, setStatusVisible] = useState(true);
  const [statusEntering, setStatusEntering] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  // Cycle through status messages every 2.8s with fade
  useEffect(() => {
    if (!isLoading) return;

    const cycle = () => {
      setStatusVisible(false);
      setStatusEntering(false);
      setTimeout(() => {
        setStatusIndex((i) => (i + 1) % UPLOAD_STATUS_KEYS.length);
        setStatusVisible(true);
        setStatusEntering(true);
        // Trigger "entering" animation in callback: paint at +6px then transition to 0
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setStatusEntering(false));
        });
      }, FADE_MS);
    };

    const id = setInterval(cycle, MESSAGE_CYCLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isLoading]);

  const handleCloseClick = () => {
    if (isLoading) return;
    onTimeoutClose();
  };

  const handleCloseMouseEnter = () => {
    if (!isLoading) return;
    setTooltipVisible(true);
  };

  const handleCloseMouseLeave = () => {
    setTooltipVisible(false);
  };

  const canClose = !isLoading;

  return (
    <SalonModal tag={tag} isOpen={isOpen} withFooter shouldCloseOnOverlayClick={canClose} shouldCloseOnEsc={canClose}>
      <SalonModal.Header>
        <div className={cn(SM.closeButtonContainer, CN.closeButtonWrapper, { [CN.closeDisabled]: isLoading })}>
          {isLoading && (
            <div
              className={CN.closeButtonOverlay}
              onMouseEnter={handleCloseMouseEnter}
              onMouseLeave={handleCloseMouseLeave}
              aria-hidden
            />
          )}
          <Button variant="empty" onClick={handleCloseClick} disabled={isLoading} aria-disabled={isLoading}>
            <Icon variant="close" variantInvert />
          </Button>
        </div>
      </SalonModal.Header>

      <SalonModal.Body>
        <div className={CN.bodyContentWrap}>
          {tooltipVisible && (
            <div className={CN.uploadTooltipInModal} role="tooltip">
              {t('clientsEmailPage.topBar.estimateModal.uploadTooltip')}
            </div>
          )}
          <IconStatus variant="info" width="lg">
            <Icon variant="envelope" width="l1" variantInvertDark />
          </IconStatus>
          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>{t('clientsEmailPage.topBar.estimateModal.sendTitle')}</div>
            <div className={CN.modalSubTitle}>{t('clientsEmailPage.topBar.estimateModal.sendSubTitle')}</div>
            <div className={CN.estimateBoxContainer}>
              <div className={CN.box}>
                <div className={CN.label}>{t('clientsEmailPage.topBar.estimateModal.contacts')}</div>
                <div className={CN.value}>{expectedCount ?? '-'}</div>
              </div>
              <div className={CN.box}>
                <div className={CN.label}>{t('clientsEmailPage.topBar.estimateModal.costPerEmail')}</div>
                <div className={CN.value}>
                  <CurrencySign />
                  {cosPerEmail}
                </div>
              </div>
              <div className={CN.box}>
                <div className={CN.label}>{t('clientsEmailPage.topBar.estimateModal.totalCost')}</div>
                <div className={CN.value}>
                  <CurrencySign value={expectedCost} />
                </div>
              </div>
            </div>
            <br />

            <div className={CN.alertFirstEmail}>
              {S.lamp}
              <Space bold>{t('clientsEmailPage.topBar.estimateModal.firstBlastTitle')} </Space>
              {t('clientsEmailPage.topBar.estimateModal.firstBlastText')}
            </div>

            {hasNoClients && (
              <div className={SM.error}>{t('clientsEmailPage.topBar.estimateModal.noClientsFound')}</div>
            )}

            <div
              className={cn({
                [SM.modalText]: true,
                [SM.widthMd]: true,
              })}
            >
              {hasNoClients
                ? t('clientsEmailPage.topBar.estimateModal.uploadContacts')
                : t('clientsEmailPage.topBar.estimateModal.createCampaign')}
            </div>
          </div>
        </div>
      </SalonModal.Body>
      <SalonModal.Footer>
        {isFine && (
          <div className={SM.modalButtons}>
            <Button tag="EstimateModal__cancel-button" onClick={onClose} variant="outline" noMargin xSpace>
              {t('clientsEmailPage.topBar.estimateModal.cancel')}
            </Button>
            <Button
              tag="EstimateModal__design-button"
              variant="primary"
              noMargin
              xSpace
              onClick={onDesign}
              disabled={disabled}
            >
              {isDesignCreating ? (
                <div className={CN.flexRow}>
                  {t('clientsEmailPage.topBar.estimateModal.loading')}
                  <InlineSpinner variant="light" />
                </div>
              ) : (
                t('clientsEmailPage.topBar.estimateModal.confirm')
              )}
            </Button>
          </div>
        )}

        {isLoading && (
          <div className={CN.infoState}>
            <InlineSpinner variant="light" />
            <div className={CN.uploadStatusWrap}>
              <div
                className={cn(CN.statusLine, {
                  [CN.statusLineOut]: !statusVisible,
                  [CN.statusLineVisible]: statusVisible,
                  [CN.statusLineEntering]: statusEntering,
                })}
                key={UPLOAD_STATUS_KEYS[statusIndex]}
              >
                {t(UPLOAD_STATUS_KEYS[statusIndex])}
              </div>
              <div className={CN.statusSubtext}>{t('clientsEmailPage.topBar.estimateModal.uploadSubtext')}</div>
              <div className={CN.progressBarTrack}>
                <div className={CN.progressBarFill} style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          </div>
        )}

        {isComplete && (
          <div className={CN.footerCompleteWrap}>
            <div className={CN.completeState}>
              <div className={CN.completeMessage}>
                {t('clientsEmailPage.topBar.estimateModal.uploadCompleteMessage')}
              </div>
              <div className={CN.completeSubtext}>
                {t('clientsEmailPage.topBar.estimateModal.uploadCompleteSubtext')}
              </div>
              <div className={CN.progressBarTrack}>
                <div className={cn(CN.progressBarFill, CN.progressBarFillComplete)} />
              </div>
            </div>
          </div>
        )}
      </SalonModal.Footer>
    </SalonModal>
  );
};

export default EstimateModal;
